import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import { fontMedium } from "swing-components";

import { ContentSingleColumn, useAuth } from "~components";
import { TaskCard } from "~onboarding/components";
import { computeGroupsState, getConfiguration, type TaskComputed } from "~onboarding/utils";
import { createMoreUrl, createPermitTaskURL } from "~pages";
import { GET, SubAttribute, translate } from "~utils";

/***** Constants *****/
/**
 * Onboarding configuration IDs that are allowed to access this permit page.
 *
 * This page is specifically designed for users with the CA UKG Swing Cert
 * onboarding flow who want to view and complete optional tasks outside of
 * the main onboarding process.
 */
const ALLOWED_ONBOARDING_CONFIGURATION_IDS = ["ca-ukg-swing-cert"] as const;

/***** Types *****/
// TODO: @KoltonG - Remove this once we have a proper route
// type PermitPageProps = RouteComponentProps<
//   ExtractRouteParams<(typeof AUTH_URLS)["permit"], string>
// >;

/**
 * Page | Permit
 * URL: /more/permit
 *
 * Renders only the optional Swing Cert task via the onboarding flow.
 */
export function PermitPage() {
  /*** Hooks ***/
  const { userInfo } = useAuth();
  const history = useHistory();

  /*** Access Control ***/
  const hasValidOnboardingConfig =
    userInfo.onboardingConfigurationId &&
    ALLOWED_ONBOARDING_CONFIGURATION_IDS.includes(
      userInfo.onboardingConfigurationId as (typeof ALLOWED_ONBOARDING_CONFIGURATION_IDS)[number],
    );

  // Redirect unauthorized users back to More page
  useEffect(() => {
    if (!hasValidOnboardingConfig) {
      // eslint-disable-next-line no-console
      console.error(
        `Unauthorized access attempt to permit page. User onboardingConfigurationId: "${userInfo.onboardingConfigurationId}" is not in allowed list:`,
        ALLOWED_ONBOARDING_CONFIGURATION_IDS,
      );

      history.replace(createMoreUrl());
    }
  }, [hasValidOnboardingConfig, history, userInfo.onboardingConfigurationId]);

  /*** Queries ***/
  const { data: onboardingConfiguration, isError: isConfigurationError } = useQuery({
    queryKey: ["configuration", userInfo.onboardingConfigurationId],
    queryFn: () => getConfiguration(userInfo.onboardingConfigurationId || ""),
    enabled: Boolean(userInfo.onboardingConfigurationId && hasValidOnboardingConfig),
    staleTime: Infinity,
    retry: false,
  });

  const { data: subAttributes, isError: isSubAttributesError } = useQuery({
    queryKey: ["attributes"],
    queryFn: () => GET("/api/sub/attributes"),
    select: (data) =>
      data.data.map((attribute) => ({
        ...attribute,
        updatedAt: new Date(attribute.updatedAt),
      })) as Array<SubAttribute>,
    retryDelay: 500,
    enabled: Boolean(hasValidOnboardingConfig),
  });

  // Don't render anything while redirect is happening
  if (!hasValidOnboardingConfig) {
    return null;
  }

  /*** Loading and Error States ***/
  if (isSubAttributesError || isConfigurationError) {
    return (
      <ScorePage title={translate("PERMIT_PAGE_TITLE")} hasBack>
        <ContentSingleColumn>
          <div css={{ padding: "24px 16px" }}>
            <p>Unable to load onboarding data. Please try again later.</p>
          </div>
        </ContentSingleColumn>
      </ScorePage>
    );
  }

  if (!subAttributes || !onboardingConfiguration) {
    return <ScorePage title={translate("PERMIT_PAGE_TITLE")} hasBack isLoading />;
  }

  /*** Compute Tasks ***/
  const groupsComputed = computeGroupsState({
    groups: onboardingConfiguration.groups,
    syncedSubAttributes: subAttributes,
  });

  // Filter for optional tasks only
  const optionalTasks: TaskComputed[] = groupsComputed
    .flatMap((group) => group.tasks)
    .filter((task) => task.isOptional === true);

  /*** Render ***/
  return (
    <ScorePage title={translate("PERMIT_PAGE_TITLE")} hasBack>
      <ContentSingleColumn>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingTop: "24px",
            paddingBottom: "24px",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          {/* Content */}
          <p css={{ ...fontMedium(), margin: 0 }}>{translate("PERMIT_PAGE_CONTENT")}</p>

          {/* Optional Tasks */}
          <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {optionalTasks.length === 0 ? (
              <p css={{ textAlign: "center", color: "#666", padding: "24px" }}>
                No optional tasks available at this time.
              </p>
            ) : (
              optionalTasks.map((task, taskIndex) => (
                <TaskCard
                  key={task.name}
                  {...task}
                  onClick={() => {
                    // Navigate to the task with the first step (0)
                    history.push(createPermitTaskURL(taskIndex, 0));
                  }}
                />
              ))
            )}
          </div>
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}
